import { LuCheck } from "react-icons/lu";
import { cn } from "./cn";
import { Badge } from "./Badge";

export interface RadioCardOption<K extends string> {
  value: K;
  title: string;
  description?: string;
  /** Selo ao lado do título (ex.: "Recomendado"). */
  badge?: string;
}

interface RadioCardsProps<K extends string> {
  /** Nome acessível do grupo (também usado como rótulo visível). */
  label: string;
  options: RadioCardOption<K>[];
  value: K;
  onChange: (value: K) => void;
  disabled?: boolean;
  /** 1 = um embaixo do outro (descrições longas). */
  columns?: 1 | 2;
}

// Escolha exclusiva entre poucas opções, em cartões lado a lado.
// Só título: centralizado, com ✓ ao escolher. Com descrição: à esquerda, com o círculo de seleção à direita.
export function RadioCards<K extends string>({ label, options, value, onChange, disabled, columns = 2 }: RadioCardsProps<K>) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-label font-semibold text-fg-secondary">{label}</span>
      <div role="radiogroup" aria-label={label} className={cn("grid gap-2.5", columns === 2 ? "grid-cols-2" : "grid-cols-1")}>
        {options.map((option) => {
          const checked = option.value === value;
          const detailed = Boolean(option.description);
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={cn(
                "relative min-h-control rounded-control border-strong transition-colors duration-fast",
                "focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50",
                detailed ? "py-3 pl-4 pr-12 text-left" : "flex items-center justify-center gap-2 px-3",
                checked ? "border-primary bg-primary-selected" : "border-line-strong bg-surface hover:border-primary-line"
              )}
            >
              <span className={cn("flex flex-wrap items-center gap-2 text-body font-semibold", checked ? "text-primary" : "text-fg-secondary")}>
                {!detailed && checked && <LuCheck size={16} strokeWidth={3} aria-hidden className="animate-scale-in" />}
                {option.title}
                {option.badge && <Badge tone="primary">{option.badge}</Badge>}
              </span>
              {detailed && <span className="block text-caption text-fg-muted">{option.description}</span>}
              {detailed && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute right-3.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border-2",
                    checked ? "border-primary bg-primary text-on-primary" : "border-primary-line bg-surface text-transparent"
                  )}
                >
                  <LuCheck size={14} strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
